/**
 * Admin Invitations
 * Manage beta tester invitations
 */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Mail, Plus, Send, Loader2, Copy, RefreshCw,
  Trash2, Clock, CheckCircle, User, Building2, X
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import axios from 'axios';
import { auth } from '../../utils/auth';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { nb } from 'date-fns/locale';
import { settingsApi } from '../../services';

const API = process.env.REACT_APP_BACKEND_URL;

interface Invitation {
  id: string;
  email: string;
  name?: string;
  company?: string;
  status: string;
  access_type: string;
  trial_days: number;
  created_at: string;
  sent_at?: string;
  registered_at?: string;
  expires_at: string;
  notes?: string;
}

const AdminInvitations: React.FC = () => {
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [sending, setSending] = useState(false);
  const [filter, setFilter] = useState<string>('all');
  
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    company: '',
    trial_days: 30,
    notes: ''
  });
  
  useEffect(() => {
    fetchInvitations();
  }, [filter]);
  
  const fetchInvitations = async () => {
    try {
      const token = auth.getToken();
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const response = await axios.get(`${API}/api/admin/invitations${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvitations(response.data.invitations);
    } catch (error) {
      console.error('Failed to fetch invitations:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const sendInvitation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.email) return;
    
    setSending(true);
    try {
      const token = auth.getToken();
      const response = await axios.post(`${API}/api/admin/invitations`, {
        email: formData.email,
        name: formData.name || null,
        company: formData.company || null,
        trial_days: formData.trial_days,
        notes: formData.notes || null,
        send_email: true
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Invitasjon sendt til ${formData.email}`);
      setShowModal(false);
      setFormData({ email: '', name: '', company: '', trial_days: 30, notes: '' });
      fetchInvitations();
      
      // Copy link to clipboard
      if (response.data.registration_link) {
        navigator.clipboard.writeText(response.data.registration_link);
        toast.info('Registreringslenke kopiert til utklippstavlen');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Kunne ikke sende invitasjon');
    } finally {
      setSending(false);
    }
  };
  
  const resendInvitation = async (id: string) => {
    try {
      const token = auth.getToken();
      const response = await axios.post(`${API}/api/admin/invitations/${id}/resend`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Invitasjon sendt på nytt');
      fetchInvitations();
      
      if (response.data.registration_link) {
        navigator.clipboard.writeText(response.data.registration_link);
        toast.info('Ny lenke kopiert');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Kunne ikke sende på nytt');
    }
  };
  
  const deleteInvitation = async (id: string) => {
    if (!confirm('Er du sikker på at du vil slette denne invitasjonen?')) return;
    
    try {
      const token = auth.getToken();
      await axios.delete(`${API}/api/admin/invitations/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Invitasjon slettet');
      fetchInvitations();
    } catch (error) {
      toast.error('Kunne ikke slette invitasjon');
    }
  };
  
  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      PENDING: 'bg-slate-700 text-slate-300',
      SENT: 'bg-blue-500/20 text-blue-400',
      OPENED: 'bg-amber-500/20 text-amber-400',
      REGISTERED: 'bg-emerald-500/20 text-emerald-400',
      EXPIRED: 'bg-red-500/20 text-red-400'
    };
    const labels: Record<string, string> = {
      PENDING: 'Venter',
      SENT: 'Sendt',
      OPENED: 'Åpnet',
      REGISTERED: 'Registrert',
      EXPIRED: 'Utløpt'
    };
    return (
      <span className={`text-xs px-2 py-1 rounded-full font-medium ${styles[status] || styles.PENDING}`}>
        {labels[status] || status}
      </span>
    );
  };
  
  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">Beta-invitasjoner</h1>
          <p className="text-slate-400 mt-1">Send invitasjoner til nye testbrukere</p>
        </div>
        <Button
          onClick={() => setShowModal(true)}
          className="bg-violet-600 hover:bg-violet-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Ny invitasjon
        </Button>
      </div>
      
      {/* Filters */}
      <div className="flex gap-2 mb-6">
        {['all', 'PENDING', 'SENT', 'REGISTERED'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              filter === f
                ? 'bg-violet-600 text-white'
                : 'bg-slate-800 text-slate-400 hover:text-white'
            }`}
          >
            {f === 'all' ? 'Alle' : f === 'PENDING' ? 'Venter' : f === 'SENT' ? 'Sendt' : 'Registrert'}
          </button>
        ))}
        <button
          onClick={fetchInvitations}
          className="ml-auto px-3 py-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>
      
      {/* Invitations list */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      ) : invitations.length === 0 ? (
        <div className="text-center py-12 bg-slate-900 border border-slate-800 rounded-2xl">
          <Mail className="w-12 h-12 text-slate-700 mx-auto mb-4" />
          <p className="text-slate-400">Ingen invitasjoner ennå</p>
          <Button
            onClick={() => setShowModal(true)}
            variant="outline"
            className="mt-4 border-slate-700 text-slate-300 hover:bg-slate-800"
          >
            Send din første invitasjon
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {invitations.map((inv, index) => (
            <motion.div
              key={inv.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-slate-900 border border-slate-800 rounded-xl p-4 hover:border-slate-700 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-slate-400" />
                  </div>
                  <div>
                    <div className="flex items-center gap-3">
                      <p className="font-medium text-white">{inv.email}</p>
                      {getStatusBadge(inv.status)}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                      {inv.name && <span>{inv.name}</span>}
                      {inv.company && (
                        <span className="flex items-center gap-1">
                          <Building2 className="w-3 h-3" />
                          {inv.company}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {format(new Date(inv.created_at), 'd. MMM yyyy', { locale: nb })}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  {inv.status !== 'REGISTERED' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => resendInvitation(inv.id)}
                      className="text-slate-400 hover:text-white"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteInvitation(inv.id)}
                    className="text-slate-400 hover:text-red-400"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              {inv.notes && (
                <p className="mt-3 text-sm text-slate-500 bg-slate-800/50 rounded-lg px-3 py-2">
                  {inv.notes}
                </p>
              )}
            </motion.div>
          ))}
        </div>
      )}
      
      {/* New invitation modal */}
      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/70"
              onClick={() => setShowModal(false)}
            />
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-md overflow-hidden"
            >
              <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-white">Ny beta-invitasjon</h2>
                <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <form onSubmit={sendInvitation} className="p-6 space-y-4">
                <div>
                  <Label className="text-slate-300">E-post *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="bruker@firma.no"
                    className="mt-1 bg-slate-800 border-slate-700 text-white"
                    required
                  />
                </div>
                
                <div>
                  <Label className="text-slate-300">Navn</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ola Nordmann"
                    className="mt-1 bg-slate-800 border-slate-700 text-white"
                  />
                </div>
                
                <div>
                  <Label className="text-slate-300">Firma</Label>
                  <Input
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    placeholder="Advokatfirma AS"
                    className="mt-1 bg-slate-800 border-slate-700 text-white"
                  />
                </div>
                
                <div>
                  <Label className="text-slate-300">Prøveperiode (dager)</Label>
                  <Input
                    type="number"
                    value={formData.trial_days}
                    onChange={(e) => setFormData({ ...formData, trial_days: parseInt(e.target.value) || 30 })}
                    className="mt-1 bg-slate-800 border-slate-700 text-white w-24"
                  />
                </div>
                
                <div>
                  <Label className="text-slate-300">Notat (valgfritt)</Label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Intern merknad..."
                    className="mt-1 w-full bg-slate-800 border border-slate-700 text-white rounded-lg px-3 py-2 text-sm resize-none h-20"
                  />
                </div>
                
                <Button
                  type="submit"
                  disabled={sending || !formData.email}
                  className="w-full bg-violet-600 hover:bg-violet-700"
                >
                  {sending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send invitasjon
                    </>
                  )}
                </Button>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminInvitations;
